import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Text, Spinner } from "@radix-ui/themes";
import { useHandleGoogleOAuthCallback } from "hooks";

export function GoogleReviewsCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);
  
  const handleCallback = useHandleGoogleOAuthCallback();

  useEffect(() => {
    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const errorParam = searchParams.get("error");

      console.log("OAuth Callback - code:", code ? "present" : "missing");
      console.log("OAuth Callback - state from URL:", state);

      // Check for OAuth error
      if (errorParam) {
        setError(`Google authentication failed: ${errorParam}`);
        setTimeout(() => {
          navigate("/integrations/browse-all");
        }, 3000);
        return;
      }

      // Get stored values from sessionStorage
      const storedOrgId = sessionStorage.getItem("google_reviews_org_id");
      const storedState = sessionStorage.getItem("google_reviews_state");

      console.log("OAuth Callback - stored state:", storedState);
      console.log("OAuth Callback - stored orgId:", storedOrgId);

      if (!code || !state) {
        setError("Missing authorization code or state");
        setTimeout(() => {
          navigate("/integrations/browse-all");
        }, 3000);
        return;
      }

      // Verify state matches (CSRF protection)
      if (state !== storedState) {
        console.error("State mismatch - URL state:", state, "Stored state:", storedState);
        setError("Invalid state token. Please try again.");
        setTimeout(() => {
          navigate("/integrations/browse-all");
        }, 3000);
        return;
      }

      try {
        const result = await handleCallback.mutateAsync({
          code: code,
          state: state,
          redirect_uri: `${window.location.origin}/integrations/google-reviews/callback`,
          organisation_id: storedOrgId,
        });

        console.log("OAuth Callback - API response:", result?.data);

        if (result?.data?.status === "success") {
          // Clear sessionStorage
          sessionStorage.removeItem("google_reviews_org_id");
          sessionStorage.removeItem("google_reviews_state");

          // Navigate to setup page
          navigate("/google-reviews-setup", {
            state: {
              orgId: storedOrgId,
              isManaging: false,
            },
          });
        } else {
          setError(result?.data?.message || "Failed to complete authentication");
          setTimeout(() => {
            navigate("/integrations/browse-all");
          }, 3000);
        }
      } catch (err) {
        console.error("OAuth callback error:", err);
        console.error("Error response:", err.response?.data);
        setError(err.response?.data?.message || "Failed to complete authentication");
        setTimeout(() => {
          navigate("/integrations/browse-all");
        }, 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate]); // Removed handleCallback from dependencies

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="4"
      style={{ height: "100vh", width: "100%" }}
    >
      {error ? (
        <>
          <Text size="5" weight="bold" color="red">
            Authentication Error
          </Text>
          <Text size="3" color="gray">
            {error}
          </Text>
          <Text size="2" color="gray">
            Redirecting back...
          </Text>
        </>
      ) : (
        <>
          <Spinner size="3" />
          <Text size="4" weight="medium">
            Connecting to Google Business Profile...
          </Text>
          <Text size="2" color="gray">
            Please wait while we complete the authentication.
          </Text>
        </>
      )}
    </Flex>
  );
}
